import { PRODUCT_NAME } from '@classgo/shared/brand';

/**
 * Strings shared by every app: sign-in and recovery screens, session states, API and auth
 * errors, common words. Spanish of Colombia, institutional register (usted). Apps merge their own
 * catalogue on top (`{ ...kitMessages, ...appMessages }`).
 */
export const kitMessages = {
  'common.loading': 'Cargando…',
  'common.back': 'Volver',
  'invite.title': 'Cree su contraseña',
  'invite.subtitle': `{school} le invitó a ${PRODUCT_NAME} como {role}, {name}.`,
  'invite.email': 'Ingresará con el correo {email}.',
  'invite.students': 'Verá la información de:',
  'invite.assignments': 'Sus grupos este año:',
  'invite.homeroom': 'dirección de grupo',
  'invite.submit': 'Crear contraseña y entrar',
  'invite.role.SCHOOL_OWNER': 'rectoría',
  'invite.role.SCHOOL_STAFF': 'personal administrativo',
  'invite.role.TEACHER': 'docente',
  'invite.role.GUARDIAN': 'acudiente',
  'invite.notFound.title': 'Invitación no encontrada',
  'invite.notFound.help': 'El enlace no es válido. Pida a su colegio una invitación nueva.',
  'invite.accepted.title': 'Esta invitación ya se usó',
  'invite.accepted.help': 'Su cuenta ya existe: inicie sesión con su contraseña.',
  'invite.expired.title': 'Esta invitación venció',
  'invite.expired.help': 'Los enlaces valen siete días. Pida a su colegio que la reenvíe.',
  'invite.revoked.title': 'Hay una invitación más reciente',
  'invite.revoked.help': 'Use el enlace del último correo que recibió del colegio.',
  'login.title': 'Iniciar sesión',
  'login.subtitle': 'Ingrese con el correo que le asignó su colegio.',
  'login.email': 'Correo electrónico',
  'login.password': 'Contraseña',
  'login.submit': 'Ingresar',
  'login.forgot': '¿Olvidó su contraseña?',
  'login.newPassword.title': 'Cree su contraseña',
  'login.newPassword.help':
    'Su contraseña temporal solo sirve una vez. Elija una permanente para continuar.',
  'login.newPassword.field': 'Nueva contraseña',
  'login.newPassword.confirm': 'Confirme la contraseña',
  'login.newPassword.mismatch': 'Las contraseñas no coinciden.',
  'login.newPassword.submit': 'Guardar y continuar',
  'login.totp.title': 'Código de verificación',
  'login.totp.help': 'Ingrese el código de seis dígitos de su aplicación de autenticación.',
  'login.totp.code': 'Código',
  'login.totp.submit': 'Verificar',
  'login.totpSetup.title': 'Active la verificación en dos pasos',
  'login.totpSetup.help':
    'Agregue esta clave en su aplicación de autenticación (Google Authenticator, 1Password, Authy) y escriba el primer código que genere.',
  'login.totpSetup.secret': 'Clave para la aplicación',
  'login.totpSetup.submit': 'Activar y continuar',
  'mfa.title': 'Active la verificación en dos pasos',
  'mfa.help':
    'Su rol exige un segundo factor. Escanee el código con su aplicación de autenticación (Google Authenticator, 1Password, Authy) o escriba la clave, y luego ingrese el primer código que genere.',
  'mfa.qrAlt': 'Código QR para la aplicación de autenticación',
  'mfa.secret': 'Clave para la aplicación',
  'mfa.code': 'Código de seis dígitos',
  'mfa.submit': 'Activar y continuar',
  'mfa.recovery': `Si pierde su aplicación de autenticación, el equipo de ${PRODUCT_NAME} puede restablecer este paso.`,
  'forgot.title': 'Recuperar contraseña',
  'forgot.help': 'Le enviaremos un código al correo registrado en su colegio.',
  'forgot.submit': 'Enviar código',
  'forgot.sent': 'Si el correo existe, enviamos un código. Revise su bandeja de entrada.',
  'forgot.code': 'Código recibido',
  'forgot.newPassword': 'Nueva contraseña',
  'forgot.confirm': 'Cambiar contraseña',
  'forgot.done': 'Su contraseña fue cambiada. Ya puede iniciar sesión.',
  'forgot.backToLogin': 'Volver a iniciar sesión',
  'password.policy': 'Mínimo 12 caracteres, con mayúsculas, minúsculas y números.',
  'auth.error.INVALID_CREDENTIALS': 'Correo o contraseña incorrectos.',
  'auth.error.USER_NOT_FOUND': 'Correo o contraseña incorrectos.',
  'auth.error.PASSWORD_RESET_REQUIRED':
    'Debe restablecer su contraseña. Use "¿Olvidó su contraseña?".',
  'auth.error.CODE_MISMATCH': 'El código no es válido.',
  'auth.error.CODE_EXPIRED': 'El código expiró. Solicite uno nuevo.',
  'auth.error.INVALID_PASSWORD': 'La contraseña no cumple la política.',
  'auth.error.TOO_MANY_ATTEMPTS': 'Demasiados intentos. Espere unos minutos e intente de nuevo.',
  'auth.error.NETWORK': 'Sin conexión. Revise su red e intente de nuevo.',
  'auth.error.UNKNOWN': 'No se pudo completar. Intente de nuevo.',
  'api.error.UNAUTHORIZED': 'Su sesión terminó. Ingrese de nuevo.',
  'api.error.FORBIDDEN': 'No tiene permiso para esta acción.',
  'api.error.NOT_FOUND': 'No encontramos lo que busca.',
  'api.error.VALIDATION_FAILED': 'Revise los datos ingresados.',
  'api.error.CONFLICT': 'Ya existe un registro con esos datos.',
  'api.error.NETWORK': 'Sin conexión. Revise su red e intente de nuevo.',
  'api.error.INTERNAL': 'Ocurrió un error en el servidor. Intente de nuevo.',
  'api.error.UNAVAILABLE': 'El servidor está despertando. Espere un momento.',
  'waking.title': 'Despertando el servidor…',
  'api.error.UNKNOWN': 'No se pudo completar. Intente de nuevo.',
  'session.expired': 'Su sesión terminó. Ingrese de nuevo para continuar.',
  'session.notProvisioned.title': 'No encontramos su usuario',
  'session.notProvisioned.body': `Su cuenta existe pero no está asignada a ningún colegio. Contacte a la administración de su colegio o al equipo de ${PRODUCT_NAME}.`,
  'session.error.title': 'No pudimos cargar su sesión',
  'session.suspended.title': 'Este colegio está suspendido',
  'session.suspended.body': `El acceso de su colegio a ${PRODUCT_NAME} está suspendido, así que no se puede ver ni cambiar nada por ahora. Si cree que es un error, comuníquese con ${PRODUCT_NAME}.`,
  'session.disabled.title': 'Su acceso está desactivado',
  'session.disabled.body':
    'La rectoría de su colegio desactivó su usuario. Si cree que es un error, hable con ella.',
  'session.retry': 'Intentar de nuevo',
  'shell.userMenu': 'Menú de usuario',
  'shell.logout': 'Cerrar sesión',
  'shell.loggedOut': 'Sesión cerrada en todos sus dispositivos.',
} as const;
